import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Phone, ChevronRight } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { LOGO_PRIMARY, DEALERSHIP_INFO, LOGO_SIZES } from '../config/dealership';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  const navLinks = [
    { label: 'Home', href: '/' },
    { label: 'Stock', href: '/cars' },
    { label: 'Sell Your Car', href: '/sell-your-car' },
    { label: 'Service', href: '/repairs' },
    { label: 'Contact', href: '/contact' },
  ];

  const isHomePage = location.pathname === '/';

  return (
    <>
      <motion.header
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        className={`fixed top-0 w-full z-50 transition-all duration-500 ${
          isScrolled || !isHomePage
            ? 'glass-dark border-b border-white/5'
            : 'bg-transparent'
        }`}
        data-testid="main-header"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex items-center justify-between h-20 lg:h-24">
            
            {/* Logo */}
            <Link
              to="/"
              className="relative z-10 flex items-center"
              data-testid="logo-link"
            >
              <motion.img 
                whileHover={{ scale: 1.02 }}
                src={LOGO_PRIMARY}
                alt={DEALERSHIP_INFO.name}
                style={{ 
                  height: `${LOGO_SIZES.header.mobile}px`,
                }}
                className="w-auto lg:hidden"
                data-testid="header-logo-mobile"
              />
              <motion.img 
                whileHover={{ scale: 1.02 }}
                src={LOGO_PRIMARY}
                alt={DEALERSHIP_INFO.name}
                style={{ 
                  height: `${LOGO_SIZES.header.desktop}px`,
                }}
                className="w-auto hidden lg:block"
                data-testid="header-logo"
              />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-10" data-testid="desktop-nav">
              {navLinks.map((link, index) => (
                <motion.div
                  key={link.label}
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 * index + 0.3 }}
                >
                  <Link
                    to={link.href}
                    className={`relative text-sm font-medium tracking-wide transition-colors duration-300 ${
                      location.pathname === link.href 
                        ? 'text-[#CCAD60]' 
                        : 'text-zinc-400 hover:text-white'
                    }`}
                    data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {link.label}
                    {location.pathname === link.href && (
                      <motion.div
                        layoutId="nav-indicator"
                        className="absolute -bottom-1 left-0 right-0 h-px bg-[#CCAD60]"
                      />
                    )}
                  </Link>
                </motion.div>
              ))}
            </nav>

            {/* Desktop CTA */}
            <div className="hidden lg:flex items-center gap-6">
              <a
                href={DEALERSHIP_INFO.phoneHref}
                className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors"
                data-testid="header-phone"
              >
                <Phone size={16} strokeWidth={1.5} />
                <span className="text-sm font-medium">{DEALERSHIP_INFO.phone}</span>
              </a>
              <Link
                to="/cars"
                className="btn-gold text-xs px-6 py-3"
                data-testid="header-cta"
              >
                View Stock
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="lg:hidden relative z-10 p-2 text-white"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              data-testid="mobile-menu-button"
              aria-label="Toggle menu"
            >
              <AnimatePresence mode="wait">
                {isMobileMenuOpen ? (
                  <motion.div
                    key="close"
                    initial={{ rotate: -90, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    exit={{ rotate: 90, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <X size={24} />
                  </motion.div>
                ) : (
                  <motion.div
                    key="menu"
                    initial={{ rotate: 90, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    exit={{ rotate: -90, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <Menu size={24} />
                  </motion.div>
                )}
              </AnimatePresence>
            </button>
          </div>
        </div>
      </motion.header>

      {/* Mobile Navigation - Full Screen Overlay */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 lg:hidden"
            data-testid="mobile-nav"
          >
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-[#000000]"
              onClick={() => setIsMobileMenuOpen(false)}
            />
            
            {/* Menu Content */}
            <motion.nav
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'tween', duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className="absolute inset-0 flex flex-col pt-24 px-6 pb-8 bg-[#000000]"
            >
              {/* Nav Links */}
              <div className="flex-1 flex flex-col justify-center">
                {navLinks.map((link, index) => (
                  <motion.div
                    key={link.label}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    transition={{ delay: 0.1 * index }}
                  >
                    <Link
                      to={link.href}
                      className={`group flex items-center justify-between py-4 border-b border-white/5 ${
                        location.pathname === link.href 
                          ? 'text-[#CCAD60]' 
                          : 'text-white'
                      }`}
                      onClick={() => setIsMobileMenuOpen(false)}
                      data-testid={`mobile-nav-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      <span className="font-serif text-2xl">{link.label}</span>
                      <ChevronRight 
                        size={20} 
                        className="text-zinc-600 group-hover:text-[#CCAD60] group-hover:translate-x-1 transition-all" 
                      />
                    </Link>
                  </motion.div>
                ))}
              </div>

              {/* Mobile Footer */}
              <div className="mt-auto pt-8 border-t border-white/5">
                <a
                  href={DEALERSHIP_INFO.phoneHref}
                  className="flex items-center gap-3 text-[#CCAD60] mb-6"
                >
                  <Phone size={20} />
                  <span className="text-lg font-medium">{DEALERSHIP_INFO.phone}</span>
                </a>
                <Link
                  to="/cars"
                  className="btn-gold w-full justify-center"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  View Stock
                </Link>
              </div>
            </motion.nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;
